import { cloneDeep, isEqual } from "lodash";
import ModifierItem from "@/models/modifier-item";
import checkout from "./checkout/index";
import discount from "./discount/index";
import oapi from "./oapi/index";
import PPI_amenities from "./PPI_amenities/index";
import { roundOffByBankApproach, trackCartAction } from "@/utils";
import apiService from "@/../api/rest";
import {
  ERROR_GET_AMENITIES,
  ERROR_AMENITY_ALREADY_APPLIED,
  ERROR_AMENITY_INELIGIBLE,
  ERROR_PROCESSING_PAYMENT,
  SKIDATA_PAYMENT_INFO,
  OAPI_API_TYPE,
  SENTRY_ERRORS
} from "../../../../app-constants";
import { isItemAvailableDayParts, shouldItemBeDisabled } from "../vendor/menu-items";
import { mapPosInfo } from "@appetize/iw-common/mappers/ppiMappers";
import { PPI_PAYMENT_TYPE_MEAL, PPI_PAYMENT_TYPE_MEAL_EQUIV } from "@appetize/iw-common/constants";
import errorLoggerAdapter from "@/adapters/errorLoggerAdapter";
import { TrackEventField } from "@/models/utils/interfaces/pages-track";
import { v4 as uuidv4 } from "uuid";

export const getDefaultState = () => {
  return {
    cartItemLocations: {},
    cartItems: [],
    disabledCartItems: [],
    tip: 0,
    tipPercentage: 0, //0 unset, -1 Custom
    params: null,
    ppiAmenities: [],
    ppiReductions: [],
    ppiMAPIReductionApplied: false,
    deviceOrderId: "",
    roundUpForCharity: false
  };
};

// initial state
export const state = getDefaultState();

// getters
const getters = {
  getRoundUpForCharity(state) {
    return state.roundUpForCharity;
  },
  getRoundUpForCharityAmount(state, getters) {
    const total = getters.getOAPISubTotal + getters.getOAPItotalTaxes + getters.getOAPITotalFees;
    const roundedTotal = Math.trunc(total + 1);
    const amount = roundedTotal - total;
    return Number(amount.toFixed(2));
  },
  getPpiReductions(state) {
    return state.ppiReductions;
  },
  //Getter user by mapi only.
  //Returns only the first reduction of the first card applied.
  getFirstreductionId(state) {
    if (state.ppiReductions[0]?.reductions?.length > 0) {
      return state.ppiReductions[0].reductions[0].reductionId.toString() || null;
    }
    return null;
  },
  getPpiAmenities(state) {
    return state.ppiAmenities;
  },
  getCartItems(state) {
    return state.cartItems;
  },
  mapAdditonalItems() {
    return (cartItem) => {
      const modifiersMap = {};
      const mapModifiers = (item) => {
        item?.modifierGroups?.forEach((modifierGroup) => {
          const selectedMods = modifierGroup.multipleChoice ? modifierGroup.selectedModifiers : [modifierGroup.selectedModifiers];
          modifierGroup.modifiers.forEach((modifier) => {
            if (selectedMods.find((modifierId) => modifier.modifierId === modifierId)) {
              modifiersMap[modifier.uuid] = modifier;
              mapModifiers(modifier);
            }
          });
        });
      };

      mapModifiers(cartItem);
      return modifiersMap;
    };
  },
  getAvailableCartItems(state) {
    return state.cartItems.filter((item) => item.active);
  },
  getUnavailableCartItems: (state, getters, rootState, rootGetters) => (pickUpTime) => {
    const actualDayParts = rootGetters["dayParts/getDayPartsBySelectedTime"](pickUpTime);
    const { cartItems = [] } = state;
    return cartItems
      .map((cartItem, index) => ({ ...cartItem?.item, index }))
      .filter((item) => !isItemAvailableDayParts(item, actualDayParts));
  },
  /**
   * Returns an array of items that are present in the current users cart but either
   * not in the current menu items, prices, mods, or mod pricing doesn't match, or not active.
   * @param {Object} state
   * @returns {function(*): *}
   */
  getDisabledCartItems(state) {
    return (cartItems) => {
      // local copy to ensure it's not cleared when the state is cleared.
      const disabledIds = [...state.disabledCartItems];
      return cartItems.filter((cartItem) => disabledIds.indexOf(cartItem.item.id) > -1).map((cartItem) => cartItem.item);
    };
  },

  /**
   * Returns an array of only alcohol items from current users cart
   * @returns {function(*): *}
   */
  getAlcoholItemsFromCart() {
    return (cartItems) => cartItems.filter((cartItem) => cartItem.item.isAlcohol).map((cartItem) => cartItem.item);
  },
  getCartSize(state) {
    return state.cartItems.reduce((size, item) => {
      return size + item.quantity;
    }, 0);
  },
  getCartItemsSubvendorLocations(state) {
    return state.cartItemLocations;
  },
  getSubTotal(state, getters, rootState, rootGetters) {
    if (rootGetters["venueSettings/APIType"] === OAPI_API_TYPE) {
      return () => rootGetters["cart/getOAPISubTotal"];
    }
    return (cartItems) => {
      cartItems = cartItems || getters.getAvailableCartItems;
      return cartItems.reduce((subtotal, product) => {
        return subtotal + (parseFloat(product.item.cost) * product.quantity + getters.getAdditionalItemsCost(product));
      }, 0);
    };
  },
  getItemSubTotal(state, getters) {
    return (product) => {
      return parseFloat(product.item.cost) * product.quantity + getters.getAdditionalItemsCost(product);
    };
  },
  getAlreadyInCartIndex(state) {
    return (item, specialInstructions) => {
      return state.cartItems.findIndex((product) => {
        return (
          product.item.id === item.id &&
          product.specialInstructions === specialInstructions &&
          isEqual(product.item.modifierGroups, item.modifierGroups)
        );
      });
    };
  },
  getAdditionalItemsCost(state, getters) {
    return (cartItem) => {
      return getters.getAdditionalItems(cartItem).reduce((price, additionalItem) => {
        return price + parseFloat(additionalItem.cost) * additionalItem.quantity;
      }, 0);
    };
  },
  isAlcoholLimitReached(state, getters, rootState, rootGetters) {
    return (quantity = 1) => {
      const alcoholDrinksInTheCart = state.cartItems.reduce(
        (sum, product) => sum + (product.item.isAlcohol ? product.quantity : 0),
        0
      );

      return quantity + alcoholDrinksInTheCart > rootGetters["vendor/maxAlcoholPerOrder"];
    };
  },
  getAdditionalItems() {
    return (cartItem) => {
      const selectedItems = Object.values(cartItem.modifiers).reduce((acc, val) => acc.concat(val), []);
      return selectedItems.map((modifier) => new ModifierItem(modifier, cartItem));
    };
  },
  getFinalPrice(state, getters, rootState, rootGetters) {
    if (rootGetters["venueSettings/APIType"] === OAPI_API_TYPE) {
      return () => roundOffByBankApproach(rootGetters["cart/getOAPITotal"] + getters.getTipValue);
    }
    return (cartItems) => {
      cartItems = cartItems || getters.getAvailableCartItems;

      const discountSubtotal = getters.getDiscountedSubTotal;
      const subTotal = discountSubtotal !== null ? discountSubtotal : getters.getSubTotal(cartItems);
      const feeValue = getters.getFeeValue(subTotal);
      const taxValue = getters.getTaxValue();
      const tipValue = getters.getTipValue;
      return roundOffByBankApproach(subTotal + feeValue + taxValue + tipValue);
    };
  },
  getFeeValue(state, getters, rootState, rootGetters) {
    if (rootGetters["venueSettings/APIType"] === OAPI_API_TYPE) {
      return () => rootGetters["cart/getOAPITotalFees"];
    }
    return (subTotal) =>
      roundOffByBankApproach(rootGetters["vendor/getVendorFee"](subTotal) + rootGetters["venue/getVenueFee"](subTotal));
  },
  getTaxValue(state, getters, rootState, rootGetters) {
    if (rootGetters["venueSettings/APIType"] === OAPI_API_TYPE) {
      return () => rootGetters["cart/getOAPItotalTaxes"];
    }

    return () => rootGetters["cart/getDiscountTaxes"];
  },
  getItemsCountInCart(state, getters) {
    return (item) => {
      return getters.getCartItems
        .filter((cartItem) => cartItem.item.id === item.id)
        .reduce((sum, cartItem) => sum + cartItem.quantity, 0);
    };
  },
  getTipValue(state) {
    return Number(state.tip);
  },
  getCheckoutParams(state) {
    return state.params;
  },
  getDeviceOrderId(state) {
    return state.deviceOrderId;
  },
  getTipPercentage(state) {
    return state.tipPercentage;
  },
  getAvailableCartMealItems(state) {
    return state.cartItems
      .filter((item) => item.active && item.item?.type === "MEAL")
      .flatMap((e) => Array(e.quantity).fill({ ...e }));
  },
  getAppliedAmenities(state, getters, rootState, rootGetters) {
    // First we set balance to the total order
    const cartItems = getters.getAvailableCartItems;
    let cartBalance = getters.getFinalPrice(cartItems);

    // Map all amenities stored in app state and store in let amenities
    // then orderBalance calculations will be done for amenities
    const amenities = state.ppiAmenities?.map((amenity) => {
      let balances = null;
      let paymentType = amenity.selectedTender.paymentType;
      let subpaymentType = amenity.selectedTender.subpaymentType;

      balances = [...amenity.balances];

      //OAPI Flow just passes the balances with no mapping nor filtering
      if (rootGetters["venueSettings/APIType"] === OAPI_API_TYPE) {
        balances = [...amenity.balances];
      } else {
        //MAPI flow
        //Skidata specific configuration
        //Skidata is payment type 31 and subpayment type 34
        if (
          amenity.selectedTender.paymentType === SKIDATA_PAYMENT_INFO.paymentType &&
          amenity.selectedTender.subpaymentType === SKIDATA_PAYMENT_INFO.subpaymentType
        ) {
          //We're looking for amenities with payment and sub payment types of 15/14
          balances = amenity.balances.filter((balance) => {
            return (
              balance.paymentType === SKIDATA_PAYMENT_INFO.balancePaymentType &&
              balance.subpaymentType === SKIDATA_PAYMENT_INFO.balanceSubPaymentType
            );
          });
          //Skidata override for MAPI checkout
          paymentType = SKIDATA_PAYMENT_INFO.MAPIPaymentType;
          subpaymentType = SKIDATA_PAYMENT_INFO.MAPISubpaymentType;
        } else {
          //If it's not skidata then we just look for balances matching payment and subpayment
          balances = amenity.balances.filter((balance) => {
            return balance.paymentType === paymentType && balance.subpaymentType === subpaymentType;
          });
        }
      }

      //Reduce balance array amounts into a single value
      const totalAmount = balances?.reduce((acc, bal) => acc + (+bal?.amount || 0), 0);
      //Reduce balance array allowToSpends into a single value
      const totalAllowToSpend = balances?.reduce((acc, bal) => acc + (+bal?.allowToSpend || 0), 0);

      return {
        id: amenity.id,
        card: amenity.card,
        selectedTender: amenity.selectedTender,
        cardBalance: {
          adjustedAmount: amenity.adjustedAmount,
          amount: totalAmount,
          allowToSpend: totalAllowToSpend,
          paymentType: paymentType,
          subpaymentType: subpaymentType
        },
        balances: balances
      };
    });

    // Keeping track of remaining meals, if a Meal tender is being used/split
    let remainingMeals = getters.getAvailableCartMealItems;
    //Loop thru the amenities to calculate balance
    const mapAppliedAmenities = amenities.map((element) => {
      let appliedAmenity = 0;

      //meal equivalencies will be treated as debits with inifinite funds, and CBORD will fail if the funds are not enough to cover the amount
      const allowToSpend =
        element.selectedTender.selectionType === PPI_PAYMENT_TYPE_MEAL_EQUIV
          ? element.cardBalance.adjustedAmount || Infinity
          : element.cardBalance.allowToSpend;

      // If the tender is Meal it is only allowed to be applied to items in the cart with a
      // type of MEAL.
      if (element.selectedTender.selectionType === PPI_PAYMENT_TYPE_MEAL) {
        const mealItems = remainingMeals;
        // Meal has a balance of # of meals, however, they are charged a dollar price for those meals
        // Calcuating the total cost of meals in the cart.
        const mealItemTotalCost = mealItems?.reduce((acc, item) => acc + item.item?.cost ?? 0, 0);
        if (!mealItems || mealItems.length === 0) {
          appliedAmenity = 0;
        } else if (mealItems?.length > allowToSpend) {
          // If the Meal tender does not cover all meals in the cart,
          // we need to calculate the cost of meals that it can and set the meals the tender can't
          // cover in the remainingMeals array so another tender can pickup that remaining balance.
          const allowedMeals = mealItems.slice(0, allowToSpend);
          const involvedMealCost = allowedMeals?.reduce((acc, item) => acc + item?.item?.cost ?? 0, 0);
          appliedAmenity = involvedMealCost;
          cartBalance -= involvedMealCost;
          remainingMeals = mealItems.slice(allowToSpend, mealItems.length);
        } else {
          appliedAmenity = mealItemTotalCost;
          remainingMeals = [];
          cartBalance -= mealItemTotalCost;
        }
      }
      //if amenity being processed wont cover the balance remaining
      //then we set the applied amenity to the whole allow to spend amount
      //and subtract allow to spend from the remaining balance
      else if (cartBalance > allowToSpend) {
        appliedAmenity = allowToSpend;
        cartBalance -= allowToSpend;
      } else {
        //If amenity cover it all then we just apply the balance
        appliedAmenity = cartBalance;
        cartBalance = 0;
      }
      return {
        ...element,
        appliedAmenity: roundOffByBankApproach(appliedAmenity),
        balance: roundOffByBankApproach(cartBalance)
      };
    });
    return { amenities: mapAppliedAmenities, balance: roundOffByBankApproach(cartBalance) };
  },
  getAmenitiesWithCardBalanceInUse(state, getters) {
    const appliedAmenities = getters.getAppliedAmenities;
    const amenities = appliedAmenities.amenities.filter((element) => element.appliedAmenity > 0);

    return {
      ...appliedAmenities,
      amenities
    };
  },
  getPpiTotal(state, getters) {
    return getters.getAppliedAmenities.amenities.reduce((acc, item) => acc + item.cardBalance.allowToSpend, 0);
  },
  isPpiMAPIReductionApplied(state) {
    return state.ppiMAPIReductionApplied;
  },
  getCartItem(state) {
    return (id) => {
      return state.cartItems.find((item) => String(item.item.id) === String(id));
    };
  },
  getFlatReductions(state, getters) {
    //get all reductions in a single array and map them to add the card number used for the validationCode
    return getters.getPpiReductions
      .map((reduction) =>
        reduction.reductions.map((red) => ({ ...red, card: reduction.card, selectedTender: reduction.selectedTender }))
      )
      .flat();
  },
  getPpiRedemptions(state, getters, rootState, rootGetters) {
    const redemptions = [];
    const appliedDiscounts = rootGetters["cart/getOAPICart"].appliedDiscounts;
    const reductions = getters.getFlatReductions;

    //loop through each of the discounts and tags returned by calculate cart
    appliedDiscounts.forEach((discount) => {
      discount.tags.forEach((tag) => {
        //check if the returned tag from calculate cart has a matching reduction then it's a redemption
        const reduction = reductions.find((reduction) => `${reduction.reductionId}` === tag);

        if (reduction) {
          redemptions.push({
            redemptionId: reduction.reductionId,
            redemptionName: discount.external_reference.name,
            redemptionValue: discount.external_reference.value,
            partnerId: reduction.partnerId,
            transactionId: reduction.transactionId,
            validationCode: reduction.card
          });
        }
      });
    });
    return redemptions;
  },
  getPpiPayments(state, getters, rootState, rootGetters) {
    const reportedAmenities = rootGetters["cart/getProcessedAmenities"].reportedAmenities || [];
    const ppiPayments = [];

    reportedAmenities.forEach((reportedAmenity) => {
      reportedAmenity.payments.forEach((payment) => {
        ppiPayments.push({
          paymentType: payment.paymentType,
          subpaymentType: payment.subpaymentType,
          spent: payment.spent,
          paymentData: payment.paymentData,
          externalId: payment.externalId,
          credential: reportedAmenity.card,
          selection: reportedAmenity.selection,
          selectionType: reportedAmenity.selectionType
        });
      });
    });

    return ppiPayments;
  }
};

// actions
const actions = {
  setRoundUpForCharity({ commit }, roundUpForCharity) {
    commit("setRoundUpForCharityData", roundUpForCharity);
  },
  /**
   * Takes in an array of menu items, grabs its index from the cartItems list,
   * and removes that item from the cartItems list. It updates cartItems list with each pass
   * to ensure the index value will be correct.
   * @param {Object} context
   * @param {Function} context.dispatch
   * @param {Object} context.getters
   * @param {Object} payload
   * @param {Item[]} payload.disabledItems
   */
  async removeDisabledItemsFromCart({ dispatch, getters }, { disabledItems }) {
    const length = disabledItems?.length - 1 || 0;
    disabledItems?.forEach((item, count) => {
      const cartItems = getters.getCartItems;
      const index = cartItems.findIndex((cartItem) => cartItem?.item?.id?.toString() === item?.id?.toString());
      if (index > -1)
        dispatch("cart/removeItemFromShoppingCart", { index, validateCartDisabled: count < length }, { root: true });
    });
  },
  addItemToCart({ commit, getters }, { item, quantity, specialInstructions, trackEvent = true }) {
    const alreadyInCartIndex = getters.getAlreadyInCartIndex(item, specialInstructions);
    const modifiers = getters.mapAdditonalItems(item);

    if (trackEvent) trackCartAction(TrackEventField.ADD, item, modifiers, quantity);

    if (alreadyInCartIndex !== -1) {
      return commit("increaseCartItemQuantity", {
        index: alreadyInCartIndex,
        quantity
      });
    }

    commit("pushItemToCart", {
      item,
      quantity,
      specialInstructions,
      modifiers
    });
  },
  editItemInCart({ commit, getters, dispatch }, { index, item, quantity, specialInstructions }) {
    const alreadyInCartIndex = getters.getAlreadyInCartIndex(item, specialInstructions);
    const modifiers = getters.mapAdditonalItems(item);

    if (alreadyInCartIndex !== -1 && alreadyInCartIndex !== index) {
      commit("increaseCartItemQuantity", {
        index: alreadyInCartIndex,
        quantity
      });

      commit("removeCartItem", index);
    } else {
      commit("updateCartItem", {
        index: index,
        item,
        quantity,
        specialInstructions,
        modifiers
      });
    }
    dispatch("cart/validateCart", null, { root: true });
  },
  removeItemFromShoppingCart({ commit, getters, dispatch }, { index, validateCartDisabled = false }) {
    const { item, modifiers, quantity } = getters.getCartItems[index];

    trackCartAction(TrackEventField.REMOVE, item, modifiers, quantity);

    commit("removeCartItem", index);
    if (getters.getCartSize === 0) {
      commit("setTip", 0);
      commit("setTipPercentage", 0);
      commit("clearPpiData");
      commit("clearProcessedAmenities", { root: true });
      dispatch("cart/clearDiscount", {}, { root: true });
      return dispatch("clearOAPICart", { root: true });
    }

    if (!validateCartDisabled) dispatch("cart/validateCart", null, { root: true });
  },
  clearShoppingCart({ commit, dispatch }) {
    return dispatch("clearDiscount").then(() => {
      commit("clearPpiData");
      commit("clearProcessedAmenities", { root: true });
      dispatch("clearOAPICart", { root: true });
      dispatch("setRoundUpForCharity", false);
      return commit("clearShoppingCart");
    });
  },
  async reviewShoppingCart({ commit, getters, dispatch, rootGetters }) {
    if (getters.getCartSize > 0) {
      const cartItemsIds = getters.getCartItems.map((item) => item.item.id);

      const fullItems = await dispatch("vendor/fetchVendorItemsByIds", cartItemsIds, { root: true });

      getters.getCartItems.forEach((cartItem, index) => {
        const menuItem = fullItems.find((menuItem) => menuItem.id === cartItem.item.id);

        // checks for difference in price, modifiers, status and ensures the item is still within the day part
        const dayParts = rootGetters["dayParts/getDayPartsBySelectedTime"]();
        if (shouldItemBeDisabled(cartItem, menuItem, dayParts)) {
          commit("setDisabledCartItem", cartItem.item.id);
        }

        if (menuItem) {
          const selectedModifiersMap = {};

          const saveSelectedModifiers = (item) => {
            item?.modifierGroups?.forEach((modifierGroup) => {
              selectedModifiersMap[modifierGroup.id] = modifierGroup.selectedModifiers;
              modifierGroup?.modifiers?.forEach((modifier) => {
                saveSelectedModifiers(modifier);
              });
            });
          };

          saveSelectedModifiers(cartItem.item);

          const restoreSelectedModifiers = (item) => {
            item?.modifierGroups?.forEach((modifierGroup) => {
              modifierGroup.selectedModifiers = selectedModifiersMap[modifierGroup.id];
              modifierGroup?.modifiers?.forEach((modifier) => {
                restoreSelectedModifiers(modifier);
              });
            });
          };

          const menuItemClone = cloneDeep(menuItem);

          restoreSelectedModifiers(menuItemClone);

          commit("overwriteCartItem", { index, item: menuItemClone });

          commit("setCartItemAvailability", { index, active: true });
        } else {
          // if item is not found, disable an item, it shouldn't take part in further calculations
          commit("setCartItemAvailability", { index, active: false });
        }
      });
    }
  },
  applyTip({ commit }, tipValue) {
    return commit("setTip", tipValue);
  },
  setCheckoutParams({ commit }, params) {
    commit("setParams", params);
  },
  setDeviceOrderId({ commit }, id) {
    commit("setDeviceOrderId", id);
  },
  clearDeviceOrderId({ commit }) {
    commit("setDeviceOrderId", "");
  },
  setSubvendorLocationData({ commit, rootGetters }, cartData) {
    const cartItems = cartData.cart.items;
    let subvendor;
    let result;

    if (cartItems && cartItems.length) {
      result = cartItems.reduce((acc, cartItem) => {
        const { item } = cartItem;
        if (parseInt(cartItem.item.subvendorId)) {
          subvendor = rootGetters["vendor/getSubvendorById"](item.subvendorId);
          if (subvendor && subvendor.location) {
            const items = acc[subvendor.location]?.items || [];
            items.push({ id: item.id, name: item.name });
            acc[subvendor.id] = {
              id: subvendor.id,
              items,
              location: subvendor.location
            };
          }
        }
        return acc;
      }, {});

      commit("setSubvendorData", result);
    }
  },
  selectTipPercentage({ commit }, tipPercentage) {
    commit("setTipPercentage", tipPercentage);
  },
  async ppiGetCardInformation({ rootGetters }, { tender, card, token = "" }) {
    try {
      const payload = {
        credentialInfo: {
          credential: card,
          pin: "",
          encoded: true,
          encType: "cnp"
        },
        posInfo: mapPosInfo(
          +rootGetters["venue/getActiveVenue"],
          +rootGetters["vendor/getActiveVendor"].id,
          +rootGetters["vendor/getActiveVendor"].defaultMobileOrderingAccountID
        ),
        token
      };
      const currentAmenities = rootGetters["cart/getPpiAmenities"];
      if (currentAmenities.some((element) => element.card === card && !element.selectedTender?.selection)) {
        return Promise.reject({ message: ERROR_AMENITY_ALREADY_APPLIED });
      }
      const response = await apiService.cardInfo(
        rootGetters["vendor/getActiveVendor"].defaultMobileOrderingAccountID, //venue or vendor
        tender.paymentType,
        tender.subpaymentType,
        payload
      );

      const cardInfo = response?.data;

      if (cardInfo.success) {
        return Promise.resolve(cardInfo);
      } else {
        return Promise.reject({ message: ERROR_GET_AMENITIES, response });
      }
    } catch (e) {
      errorLoggerAdapter.createIssue(SENTRY_ERRORS.ERROR_CARDINFO, "getPpiAmenities", "ppiGetCardInformation", {
        description: e.response
      });
      throw new Error(e.response ? ERROR_PROCESSING_PAYMENT : e);
    }
  },
  async applyPPITender({ commit, rootGetters, dispatch }, { card, balances, reductions, tender }) {
    const currentAmenities = rootGetters["cart/getPpiAmenities"];
    if (currentAmenities.some((element) => element.card === card && isEqual(element.selectedTender, tender))) {
      return Promise.reject({ message: ERROR_AMENITY_ALREADY_APPLIED });
    }

    if (tender?.selectionType === PPI_PAYMENT_TYPE_MEAL) {
      const appliedMealAmenititesTotal = rootGetters["cart/getAppliedAmenities"]?.amenities
        .filter((a) => a.selectedTender?.selectionType === PPI_PAYMENT_TYPE_MEAL)
        .reduce((acc, a) => acc + a.appliedAmenity ?? 0, 0);
      const availableCartItemsCost = rootGetters["cart/getAvailableCartMealItems"]?.reduce(
        (acc, item) => acc + item?.item?.cost ?? 0,
        0
      );
      if (appliedMealAmenititesTotal === availableCartItemsCost) {
        return Promise.reject({ message: ERROR_AMENITY_INELIGIBLE });
      }
    }

    const id = uuidv4();
    //We only push amenities when we have them
    if (balances?.length > 0) {
      commit("setAmenities", {
        balances,
        selectedTender: tender,
        card: card,
        id
      });
    }
    commit("setReductions", {
      selectedTender: tender,
      card: card,
      reductions,
      id
    });

    //Process reductions
    await dispatch("cart/processPpiReductions", {}, { root: true });
  },
  async processPpi({ dispatch }, { tender, card }) {
    const cardInfo = await dispatch("cart/ppiGetCardInformation", { tender, card }, { root: true });

    const reductions = cardInfo.reductions;
    const balances = cardInfo.balances;

    await dispatch("cart/applyPPITender", { card, balances, reductions, tender }, { root: true });
  },
  async updatePPIAmenity({ commit }, { id, amenity }) {
    commit("updateAmenityById", { id, data: amenity });
  },
  async removePPIAmenity({ commit, dispatch }, { id }) {
    commit("removeAmenityById", id);
    commit("removeReductionById", id);

    await dispatch("cart/removePpiReductions", {}, { root: true });
  },
  applyMAPIPpiReduction({ commit }) {
    commit("setMAPIPpiReductionApplied", true);
  },
  removeMAPIPpiReduction({ commit }) {
    commit("setMAPIPpiReductionApplied", false);
  },
  clearPpi({ commit, rootGetters, dispatch }) {
    if (rootGetters["cart/isPpiMAPIReductionApplied"]) {
      dispatch("cart/clearDiscount", {}, { root: true });

      const params = {
        subtotalAmount: rootGetters["cart/getSubTotal"](rootGetters["cart/getAvailableCartItems"]),
        tip: rootGetters["cart/getTipValue"],
        tax: rootGetters["cart/getTaxValue"](),
        fee: rootGetters["cart/getFeeValue"](rootGetters["cart/getDiscountedSubTotal"]),
        amount: rootGetters["cart/getFinalPrice"](rootGetters["cart/getAvailableCartItems"]),
        roundUpAmount: rootGetters["cart/getRoundUpForCharityAmount"],
        roundUpForCharity: rootGetters["cart/getRoundUpForCharity"],
        promoInfo: Object.assign({}, rootGetters["cart/getPromoInfo"]),
        cartItems: [].concat(rootGetters["cart/getAvailableCartItems"])
      };
      dispatch("cart/setCheckoutParams", params, { root: true });
    }
    commit("clearPpiData");
    commit("clearProcessedAmenities", { root: true });
  }
};

// mutations
const mutations = {
  setDisabledCartItem(state, itemId) {
    state.disabledCartItems.push(itemId);
  },
  clearDisabledCartItems(state) {
    state.disabledCartItems = [];
  },
  setRoundUpForCharityData(state, roundUpForCharity) {
    state.roundUpForCharity = roundUpForCharity;
  },
  pushItemToCart(state, item) {
    state.cartItems.push({ ...item, active: true, valid: true });
  },
  updateCartItem(state, { index, item, quantity, specialInstructions, modifiers }) {
    state.cartItems[index].quantity = quantity;
    state.cartItems[index].item = item;
    state.cartItems[index].specialInstructions = specialInstructions;
    state.cartItems[index].modifiers = modifiers;
    state.cartItems[index].valid = true;
  },
  overwriteCartItem(state, { index, item }) {
    state.cartItems[index].item = item;
  },
  setCartItemAvailability(state, { index, active }) {
    state.cartItems[index].active = active;
  },
  setCartItemValidity(state, { index, valid }) {
    state.cartItems[index].valid = valid;
  },
  increaseCartItemQuantity(state, { index, quantity }) {
    state.cartItems[index].quantity += quantity;
  },
  removeCartItem(state, index) {
    state.cartItems.splice(index, 1);
  },
  clearShoppingCart(state) {
    state.cartItems = [];
    state.tip = 0;
    state.tipPercentage = 0;
  },
  setTip(state, tipValue) {
    state.tip = tipValue;
  },
  setParams(state, params) {
    state.params = params;
  },
  setCheckoutOrder(state, order) {
    state.params.order = order;
  },
  setDeviceOrderId(state, id) {
    state.deviceOrderId = id;
  },
  setSubvendorData(state, data) {
    state.cartItemLocations = data;
  },
  setTipPercentage(state, tipPercentage) {
    state.tipPercentage = tipPercentage;
  },
  setReductions(state, data) {
    state.ppiReductions.push(data);
  },
  updateAmenityById(state, { id, data }) {
    state.ppiAmenities = state.ppiAmenities.map((amenity) => {
      if (amenity.id === id) {
        return { ...amenity, ...data };
      }
      return amenity;
    });
  },
  removeAmenityById(state, id) {
    state.ppiAmenities = state.ppiAmenities.filter((a) => a.id !== id);
  },
  removeReductionById(state, id) {
    state.ppiReductions = state.ppiReductions.filter((a) => a.id !== id);
  },
  setAmenities(state, data) {
    state.ppiAmenities.push(data);
  },
  setMAPIPpiReductionApplied(state, applied) {
    state.ppiMAPIReductionApplied = applied;
  },
  clearPpiData(state) {
    state.ppiAmenities = [];
    state.ppiReductions = [];
    state.ppiMAPIReductionApplied = false;
  },
  resetState(state) {
    Object.assign(state, getDefaultState());
  }
};

export default {
  namespaced: true,
  modules: {
    discount,
    checkout,
    oapi,
    PPI_amenities
  },
  state,
  getters,
  actions,
  mutations
};
